import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import AgentAdvantageLogo from '@propertypal/shared/src/resources/icons/agent-advantage/AgentAdvantage_logo.svg';
import EaBranding from '@propertypal/shared/src/resources/icons/products/ea-branding-icon.svg';
import EaBrochures from '@propertypal/shared/src/resources/icons/products/ea-brochures-icon.svg';
import EaCalculator from '@propertypal/shared/src/resources/icons/products/ea-calculator-icon.svg';
import EaConsultancy from '@propertypal/shared/src/resources/icons/products/ea-consultancy-icon.svg';
import EaDisplayAds from '@propertypal/shared/src/resources/icons/products/ea-display-ads-icon.svg';
import EaFeatured from '@propertypal/shared/src/resources/icons/products/ea-featured-listing-icon.svg';
import EaTakeover from '@propertypal/shared/src/resources/icons/products/ea-homepage-takeover-icon.svg';
import EaListing from '@propertypal/shared/src/resources/icons/products/ea-listing-icon.svg';
import EaPremier from '@propertypal/shared/src/resources/icons/products/ea-premier-listing-icon.svg';
import EaSponsor from '@propertypal/shared/src/resources/icons/products/ea-sponsor-search-icon.svg';
import EaSuperFeature from '@propertypal/shared/src/resources/icons/products/ea-superfeatured-homepage-icon.svg';
import EaWebsite from '@propertypal/shared/src/resources/icons/products/ea-websites-icon.svg';
import EaBoost from '@propertypal/shared/src/resources/icons/products/premier-boost-icon.svg';
import Link from 'next/link';
import React from 'react';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Heading, Text } from '../typography';
import { ProductsContent } from './ProductsContent.style';
import { ProductsGrid, AgentsGridItem, ProductsBadge, ProductBadge } from './ProductsGrid.style';
import { WhyHeading } from './WhyPropertyPalContent.style';

type Icon = {
  SVG: string;
  heading: string;
  text: string;
  maxW?: number;
  href: string;
  badge?: ProductsBadge;
};

const PRODUCTS_URL = '/products/estate-agents';

const icons: Icon[] = [
  {
    SVG: EaListing,
    heading: 'Listing a Property',
    text: `List your property on Northern Ireland's largest property website.`,
    maxW: 350,
    href: `${PRODUCTS_URL}/listing-a-property`,
  },
  {
    SVG: AgentAdvantageLogo,
    heading: 'Agent Advantage',
    text: 'A suite of tools built exclusively for property professionals in Northern Ireland.',
    href: '/agent-advantage',
  },
  {
    SVG: EaPremier,
    heading: 'Premier Listing',
    text: 'Show off your best features to stand out from the crowd.',
    maxW: 340,
    badge: {
      color: 'purple',
      title: '350%',
      text: 'more enquiries',
    },
    href: `${PRODUCTS_URL}/premier-listing`,
  },
  {
    SVG: EaBoost,
    heading: 'Premier Boost',
    text: 'Increase views of your property with two boosts up the search results',
    maxW: 330,
    href: `${PRODUCTS_URL}/premier-boost`,
  },
  {
    SVG: EaFeatured,
    heading: 'Featured Listing',
    text: 'Attract buyers with this bigger advert and extra photos.',
    maxW: 270,
    badge: {
      color: 'darkBlue',
      title: '150%',
      text: 'more enquiries',
    },
    href: `${PRODUCTS_URL}/featured-listing`,
  },
  {
    SVG: EaWebsite,
    heading: 'Websites',
    text: `Latest cutting-edge websites using PropertyPal's technology.`,
    href: '/estate-agent-websites',
  },
  {
    SVG: EaBranding,
    heading: 'Branding',
    text: 'Full service solution for marketing your business.',
    maxW: 290,
    href: `${PRODUCTS_URL}/branding`,
  },
  {
    SVG: EaBrochures,
    heading: 'Rental Lists & Brochures',
    text: 'Print perfect professional brochures for your clients at your leisure.',
    maxW: 330,
    href: `${PRODUCTS_URL}/rental-lists-and-brochures`,
  },
  {
    SVG: EaSponsor,
    heading: 'Sponsor a Search',
    text: 'Take over the search results in a prime location of your choice.',
    maxW: 280,
    href: `${PRODUCTS_URL}/sponsor-a-search`,
  },
  {
    SVG: EaDisplayAds,
    heading: 'Display Advertising',
    text: 'Targeted advertising to ensure your message reaches the right people.',
    maxW: 300,
    href: `${PRODUCTS_URL}/display-advertising`,
  },
  {
    SVG: EaCalculator,
    heading: 'Broker Calculator',
    text: 'Advertise your Mortgage Broker on your own property details pages.',
    maxW: 290,
    href: `${PRODUCTS_URL}/broker-calculator`,
  },
  {
    SVG: EaTakeover,
    heading: 'Homepage Takeover',
    text: 'Take pride of place on the PropertyPal homepage.',
    maxW: 280,
    href: `${PRODUCTS_URL}/homepage-takeover`,
  },
  {
    SVG: EaSuperFeature,
    heading: 'Superfeatured Homepage',
    text: 'Highlight exceptional properties for maximum visibility.',
    maxW: 260,
    badge: {
      color: 'primary',
      title: 'FREE',
      text: 'EXPOSURE',
    },
    href: `${PRODUCTS_URL}/superfeatured-homepage`,
  },
  {
    SVG: EaConsultancy,
    heading: 'Specialist Consultancy',
    text: 'Expertise and insight, driven by rigorous analysis and sectoral knowledge.',
    href: `${PRODUCTS_URL}/specialist-consultancy`,
  },
];

const EstateAgentsContent = () => {
  return (
    <ProductsContent>
      <WhyHeading align="center" mb={65} fontSize={36}>
        Estate Agents
      </WhyHeading>
      <ProductsGrid>
        {icons.map(({ SVG, heading, text, maxW, href, badge }) => (
          <AgentsGridItem key={heading} maxW={maxW}>
            <div>
              <SVG />
              <div>
                <Heading>{heading}</Heading>
                <Text>{text}</Text>
              </div>
            </div>
            {href && (
              <Link prefetch={false} href={href}>
                <span>Find out more</span>
                <FontAwesomeIcon icon={faChevronRight} />
              </Link>
            )}
            {badge && (
              <ProductBadge color={badge.color}>
                <span className="badge-title">{badge.title}</span>
                {badge.text && <span className="badge-text">{badge.text}</span>}
              </ProductBadge>
            )}
          </AgentsGridItem>
        ))}
      </ProductsGrid>
    </ProductsContent>
  );
};

export default EstateAgentsContent;
